import { Inject, Injectable } from '@angular/core';
import {
  DocumentData,
  Firestore,
  QuerySnapshot,
  collection,
  doc,
  getDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  getDocs,
  where,
} from 'firebase/firestore';
import { Observable, Subscriber, map } from 'rxjs';
import { User, UserId, userConverter } from '../models/user.model';
import { BehaviorSubject } from 'rxjs';
import { FIRESTORE } from '../firebase-injection';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(@Inject(FIRESTORE) private firestore: Firestore) {}
  private profileImage$ = new BehaviorSubject<string | null>(null);
  public addUser(user: User) {
    // will only be used in conjunction with the auth service, so id is already set in user object
    const userRef = doc(this.firestore, 'users', user.id).withConverter(
      userConverter
    );
    return setDoc(userRef, user);
  }

  public deleteUser(userId: UserId) {
    return deleteDoc(doc(this.firestore, 'users', userId));
  }

  public getUser(userId: UserId) {
    const userRef = doc(this.firestore, 'users', userId).withConverter(
      userConverter
    );
    return getDoc(userRef).then((doc) => {
      return doc.data();
    });
  }

  public async getUserIdByEmail(email: string) {
    const usersRef = collection(this.firestore, 'users').withConverter(
      userConverter
    );
    const q = query(usersRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs[0].data()?.id;
  }

  public getUsers() {
    return new Observable(
      (subscriber: Subscriber<QuerySnapshot<DocumentData>>) => {
        subscriber.unsubscribe = onSnapshot(
          collection(this.firestore, 'users'),
          subscriber
        );
      }
    ).pipe(
      map((querySnapshot) => {
        if (querySnapshot.empty) {
          return undefined;
        }
        return querySnapshot.docs
          .filter((doc) => doc.exists())
          .map((doc) => {
            return userConverter.fromFirestore(doc, {})!;
          });
      })
    );
  }

  public getUserByMail(email: string): Observable<User | undefined> {
    const usersRef = collection(this.firestore, 'users').withConverter(
      userConverter
    );
    const queryByMail = query(usersRef, where('email', '==', email));

    return new Observable((subscriber) => {
      const unsubscribe = onSnapshot(
        queryByMail,
        (querySnapshot) => {
          if (querySnapshot.empty) {
            subscriber.next(undefined);
          } else {
            subscriber.next(querySnapshot.docs[0].data());
          }
        },
        (error) => {
          subscriber.error(error);
        }
      );

      return () => unsubscribe();
    });
  }

  updateUser(user: User) {
    // mainly created when addUser gets more functions such as incrementing some fields
    const userRef = doc(this.firestore, 'users', user.id);
    const userObj = userConverter.toFirestore(user);
    return updateDoc(userRef, userObj);
  }

  public updateUserPartial(userId: UserId, updates: Partial<User>) {
    const userRef = doc(this.firestore, 'users', userId);
    const updateObj = userConverter.toFirestoreUpdateObject(updates);
    return updateDoc(userRef, updateObj);
  }
}
